import { motion, AnimatePresence } from "framer-motion";
import { Smartphone, Download } from "lucide-react";
import { ADDONS, BASE_RATE, computeEstimate } from "@/lib/api";

function AnimatedNumber({ value }) {
  return (
    <AnimatePresence mode="popLayout">
      <motion.span
        key={value}
        initial={{ y: 14, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -14, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        className="inline-block"
      >
        {value}
      </motion.span>
    </AnimatePresence>
  );
}

export default function QuoteCalculator({ sqft, setSqft, addons, setAddons, onBook }) {
  const estimate = computeEstimate(sqft, addons);
  const formatted = estimate.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <section id="calculator" className="py-24 md:py-32 bg-[#F6F6F2]" data-testid="calculator">
      <div className="max-w-7xl mx-auto px-6 mb-14">
        <motion.a
          href="https://customer-assets-m6fa6gv7.emergentagent.net/job_paint-job-booker/artifacts/n9t7w4b9_application-60cdbb7e-ddca-4d82-94e5-d3c46b86a887.apk"
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          whileHover={{ scale: 1.02 }}
          className="group flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-[#111] text-[#F6F6F2] rounded-3xl p-6 md:p-8 shadow-xl"
          data-testid="download-app-btn"
        >
          <div className="flex items-center gap-4">
            <span className="flex-shrink-0 w-12 h-12 rounded-2xl bg-[#005CEE] flex items-center justify-center">
              <Smartphone size={22} className="text-white" />
            </span>
            <div>
              <p className="font-mono2 text-[10px] tracking-[0.25em] text-[#FF4500]">MOBILE APP VERSION</p>
              <p className="font-head font-extrabold tracking-tight text-2xl md:text-3xl">Get the OutDoEm App</p>
              <p className="text-sm text-[#94A3B8] mt-1">Book jobs and get quotes on the go — download our Android app (.apk).</p>
            </div>
          </div>
          <span className="btn-primary inline-flex items-center gap-2 px-7 py-3.5 text-sm font-semibold whitespace-nowrap">
            <Download size={16} /> Click Me
          </span>
        </motion.a>
      </div>
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-mono2 text-xs tracking-[0.25em] text-[#005CEE] mb-4">01 / INSTANT ESTIMATE</p>
          <h2 className="font-head font-extrabold tracking-tighter text-5xl md:text-6xl leading-none">
            Know your price<br />before we knock.
          </h2>
          <p className="mt-6 text-[#8A8A85] max-w-md leading-relaxed">
            Enter your room's square footage and pick the extras you need. Our transparent
            <span className="text-[#111] font-mono2"> $1.80/sq ft</span> base rate means no surprises — just a real number, instantly.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="bg-white rounded-3xl border border-[#E5E5DF] p-8 shadow-xl"
        >
          <label className="block text-xs font-mono2 tracking-widest text-[#8A8A85] mb-3">SQUARE FOOTAGE</label>
          <div className="flex items-center gap-3 border-b-2 border-[#111] pb-3">
            <input
              type="number"
              min="0"
              value={sqft}
              onChange={(e) => setSqft(e.target.value)}
              className="w-full bg-transparent outline-none font-mono2 text-4xl md:text-5xl placeholder:text-[#E5E5DF]"
              placeholder="0"
              data-testid="calc-sqft-input"
            />
            <span className="font-mono2 text-sm text-[#8A8A85]">sq ft</span>
          </div>

          <p className="mt-6 mb-3 text-xs font-mono2 tracking-widest text-[#8A8A85]">ADD-ONS</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {ADDONS.map((a) => {
              const on = !!addons[a.key];
              return (
                <button
                  key={a.key}
                  onClick={() => setAddons({ ...addons, [a.key]: !on })}
                  className={`rounded-2xl border px-4 py-3 text-left transition-all duration-300 ${
                    on ? "bg-[#005CEE] border-[#005CEE] text-white -translate-y-0.5" : "bg-[#F6F6F2] border-[#E5E5DF] hover:border-[#111]"
                  }`}
                  data-testid={`calc-addon-${a.key}`}
                >
                  <span className="block text-sm font-semibold">{a.label}</span>
                  <span className={`font-mono2 text-xs ${on ? "text-white/80" : "text-[#8A8A85]"}`}>+{a.pct}%</span>
                </button>
              );
            })}
          </div>

          <div className="mt-8 pt-6 border-t border-[#E5E5DF] flex items-end justify-between">
            <div>
              <p className="text-xs font-mono2 tracking-widest text-[#8A8A85]">ESTIMATED TOTAL</p>
              <p className="font-mono2 text-5xl md:text-6xl mt-1 tracking-tight" data-testid="calc-estimate">
                $<AnimatedNumber value={formatted} />
              </p>
            </div>
          </div>
          <button onClick={onBook} className="btn-primary w-full mt-6 py-4 text-sm font-semibold" data-testid="calc-book-btn">
            Book This Job →
          </button>
          <p className="text-center text-[10px] text-[#8A8A85] mt-3 font-mono2">Base {`$${BASE_RATE.toFixed(2)}`}/sq ft · Final quote confirmed on site visit</p>
        </motion.div>
      </div>
    </section>
  );
}
